import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { StarRating } from './StarRating'

interface TrustpilotReviewsProps {
  reviews: {
    content: string
    source: string
    rating: number
    url?: string
  }[]
  trustpilotUrl?: string
}

export const TrustpilotReviews = ({ reviews, trustpilotUrl }: TrustpilotReviewsProps) => {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % reviews.length)
    }, 5000)

    return () => clearInterval(interval)
  }, [reviews.length])

  const handlePrev = () => {
    setIndex((prev) => (prev - 1 + reviews.length) % reviews.length)
  }

  const handleNext = () => {
    setIndex((prev) => (prev + 1) % reviews.length)
  }

  if (!reviews?.length) return null

  return (
    <section className="bg-white/5 rounded-xl p-6 text-yellow-100 text-center space-y-4 border border-white/10">
      <h2 className="text-2xl font-bold text-yellow-400 mb-4">What Traders Are Saying</h2>

      <div className="relative min-h-[140px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="space-y-2"
          >
            <p className="text-base italic">&quot;{reviews[index].content}&quot;</p>

            <div className="flex justify-center items-center gap-2 mt-2">
              <StarRating rating={reviews[index].rating} />
              <span className="text-sm text-yellow-300">{reviews[index].source}</span>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-center gap-4">
        <button
          onClick={handlePrev}
          className="bg-yellow-400 text-black rounded-full p-2 hover:bg-yellow-300 transition"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="text-sm text-yellow-300">
          {index + 1} / {reviews.length}
        </div>

        <button
          onClick={handleNext}
          className="bg-yellow-400 text-black rounded-full p-2 hover:bg-yellow-300 transition"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {trustpilotUrl && (
        <div className="text-xs text-yellow-500 mt-2">
          Verified Reviews from{' '}
          <a href={trustpilotUrl} target="_blank" rel="noopener noreferrer" className="underline text-green-400">
            Trustpilot
          </a>
        </div>
      )}
    </section>
  )
}

export default TrustpilotReviews
